import { type FC, type PropsWithChildren, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { UserInfoContext } from '@/contexts/UserInfoContext';

/**
 * Route guard component that requires the user to have an admin role.
 * If the user doesn't have an admin role, they are redirected to the home page.
 * This guard only enforces role checks when RBAC feature flag is enabled.
 * Uses server-sourced isAdmin from the session resolver (DB roles).
 *
 * Usage:
 * ```tsx
 * <Route path="/admin/personas" element={
 *   <RequireAdmin>
 *     <AdminPersonasRoute />
 *   </RequireAdmin>
 * } />
 * ```
 */
export const RequireAdmin: FC<PropsWithChildren> = ({ children }) => {
  const { userInfo } = useContext(UserInfoContext);
  const navigate = useNavigate();
  const enableRbac = !!userInfo?.features?.rbacEnabled;
  const isAdmin = !!userInfo?.isAdmin;

  useEffect(() => {
    // Only enforce RBAC if feature flag is enabled
    if (enableRbac && !isAdmin) {
      navigate('/', { replace: true });
    }
  }, [enableRbac, isAdmin, navigate]);

  // Only block access if RBAC is enabled and user doesn't have admin role
  if (enableRbac && !isAdmin) {
    return null;
  }

  return <>{children}</>;
};
